// Общата горна лента на една справка: бургер (на тесен екран), заглавие,
// часовник, презареждане и профилът.
//
// Днес всяка от 14-те справки носи собствено копие на този блок с inline
// стилове. Компонентът е нов и се ползва първо от „История на маси"; всяка
// следваща мигрирана справка сваля своето копие.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MenuOutlined, ReloadOutlined } from "@ant-design/icons";

import ProfileDropdown from "../../../components/ProfileDropdown";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { logout, setAccess } from "../../../store/authSlice";
import { denyAccess } from "../../../api/access";

/** Часовникът се обновява — досега показваше часа на последния рендер. */
const CLOCK_TICK_MS = 30_000;

type Props = {
  title: string;
  isNarrow: boolean;
  onOpenMobileMenu?: () => void;
  onRefresh: () => void;
  isFetching: boolean;
};

export const ReportTopBar = ({
  title,
  isNarrow,
  onOpenMobileMenu,
  onRefresh,
  isFetching,
}: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const access = useAppSelector((state) => state.auth.access);
  const names = useAppSelector((state) => state.auth.names);
  const username = useAppSelector((state) => state.auth.username);
  const displayName = names || username || "";

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const timeLabel = now.toLocaleTimeString(i18n.language, {
    hour: "2-digit",
    minute: "2-digit",
  });

  // На споделен терминал изходът връща до ПИН екрана, не до вход.
  const handleLogout = async () => {
    if (access) {
      try {
        await denyAccess();
      } catch {
        // Отказаният достъп е локално състояние — мрежова грешка не бива да
        // задържи човека вътре.
      }
      dispatch(setAccess(false));
      navigate("/", { replace: true });
      return;
    }
    // `await` — иначе навигацията тръгва преди `logout.fulfilled` да е изчистил
    // токена и гейтът може да пусне обратно вътре. `replace` — „назад" на
    // браузъра не бива да връща справката след изход.
    await dispatch(logout());
    navigate("/login", { replace: true });
  };

  return (
    <header
      className={`flex shrink-0 items-center justify-between gap-2.5 border-b border-shade-2 bg-surface ${
        isNarrow ? "min-h-12 px-4 py-2.5" : "min-h-[60px] px-7 py-[11px]"
      }`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2.5">
        {isNarrow ? (
          <button
            type="button"
            onClick={onOpenMobileMenu}
            aria-label={t("reportsPage.openMenu")}
            className="grid size-[34px] shrink-0 place-items-center rounded-lg text-ink transition-colors hover:bg-shade-1"
          >
            <MenuOutlined className="text-[18px]" />
          </button>
        ) : null}
        <h1
          className={`m-0 truncate font-bold leading-[1.2] tracking-[-0.5px] text-ink ${
            isNarrow ? "text-[18px]" : "text-[22px]"
          }`}
        >
          {title}
        </h1>
      </div>

      <div className={`flex shrink-0 items-center ${isNarrow ? "gap-1" : "gap-2.5"}`}>
        {!isNarrow ? (
          <span className="font-pos-mono text-[11px] tracking-[0.3px] text-muted">
            {timeLabel}
          </span>
        ) : null}
        <button
          type="button"
          onClick={onRefresh}
          title={t("reportsPage.refresh")}
          aria-label={t("reportsPage.refresh")}
          className="grid size-[30px] place-items-center rounded-md text-muted transition-colors hover:bg-shade-1 hover:text-ink"
        >
          <ReloadOutlined
            className={`text-[14px] ${isFetching ? "animate-spin" : ""}`}
          />
        </button>
        {!isNarrow ? <span className="mx-1 h-5 w-px bg-shade-2" /> : null}
        <ProfileDropdown
          displayName={displayName}
          username={username ?? ""}
          access={access}
          isNarrow={isNarrow}
          onLogout={handleLogout}
        />
      </div>
    </header>
  );
};
